/**
 * AuthCallback — /auth/google session-exchange landing page.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
 * THIS BREAKS THE AUTH.
 *
 * Flow:
 *   1. Emergent Auth redirects the rider back to
 *      {origin}/auth/google#session_id=<sid>
 *   2. We extract the session_id from the URL fragment (NOT query
 *      string — Emergent uses the hash to hide it from server logs).
 *   3. Synchronous ref-based deduplication — StrictMode double-invokes
 *      effects; we must exchange the session_id exactly ONCE.
 *   4. POST /api/auth/oauth/google/session with { session_id } → backend
 *      calls Emergent's session-data endpoint, finds-or-creates the
 *      user, issues our JWT cookie.
 *   5. Refresh the AuthContext, restore any pre-auth resume state from
 *      sessionStorage['link.auth.resume'], and navigate the rider back
 *      to exactly where they were (default: /).
 */
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/shared/lib/api";
import { useAuth } from "@/shared/context/AuthContext";

export default function AuthCallback() {
  const nav = useNavigate();
  const { refresh } = useAuth();
  const processed = useRef(false);
  const [status, setStatus] = useState("checking"); // checking | success | error
  const [errText, setErrText] = useState("");

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;

    const hash = window.location.hash || "";
    const params = new URLSearchParams(hash.startsWith("#") ? hash.slice(1) : hash);
    const sid = params.get("session_id");
    if (!sid) {
      setStatus("error");
      setErrText("Missing session id. Try signing in again.");
      return;
    }

    (async () => {
      try {
        await api.post("/auth/oauth/google/session", { session_id: sid });
        // Refresh AuthContext so downstream ProtectedRoutes see the user.
        try { await refresh?.(); } catch (_) { /* ignore */ }
        setStatus("success");
        // Restore resume state — pathname + optional wizard state.
        let target = "/";
        try {
          const raw = sessionStorage.getItem("link.auth.resume");
          if (raw) {
            const payload = JSON.parse(raw);
            if (payload?.pathname && payload.pathname !== "/auth/google") {
              target = payload.pathname;
            }
            // Restore booking wizard state (if any) — ScheduleSetup
            // reads `link.preset.route` on mount, so we bounce booking
            // state onto that same channel.
            if (payload?.state?.presetRoute) {
              try {
                localStorage.setItem(
                  "link.preset.route",
                  JSON.stringify({ ...payload.state.presetRoute, ts: Date.now() }),
                );
              } catch (_) { /* noop */ }
            }
            sessionStorage.removeItem("link.auth.resume");
          }
        } catch (_) { /* noop */ }
        // Clean URL (drop the fragment) before navigating so a refresh
        // doesn't re-trigger the callback.
        try { window.history.replaceState({}, "", "/auth/google"); } catch (_) { /* noop */ }
        nav(target, { replace: true });
      } catch (e) {
        console.error("[oauth-google] session exchange failed:", e);
        const msg = formatApiError(e?.response?.data?.detail) || e?.message || "Google sign-in failed.";
        toast.error(msg);
        setStatus("error");
        setErrText(msg);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      className="min-h-[100dvh] bg-[#F9F7F4] text-[#0A1128] flex items-center justify-center px-6"
      data-testid="auth-callback"
      data-status={status}
    >
      <div className="max-w-sm w-full text-center">
        {status !== "error" ? (
          <>
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-[#ed6500]/10 text-[#ed6500] mb-4">
              <Loader2 size={22} className="animate-spin" />
            </div>
            <h1 className="font-serif text-2xl">Signing you in…</h1>
            <p className="text-[13px] text-[#515E7B] mt-2">One moment — finishing your Google sign-in.</p>
          </>
        ) : (
          <>
            <h1 className="font-serif text-2xl text-[#0A1128]" data-testid="auth-callback-error">Sign-in failed</h1>
            <p className="text-[13px] text-[#515E7B] mt-2">{errText}</p>
            <button
              type="button"
              onClick={() => nav("/login", { replace: true })}
              className="mt-6 h-12 px-6 rounded-full bg-[#ed6500] hover:bg-[#ff7b1f] text-white text-[12px] tracking-[0.22em] uppercase font-semibold"
              data-testid="auth-callback-retry"
            >
              Back to sign in
            </button>
          </>
        )}
      </div>
    </div>
  );
}
